import { Box, Transition } from '@mantine/core';
import { forwardRef, useEffect, useState } from 'react';
import { useReducedMotion } from '../hooks';

export type PageTransitionType =
  | 'fade'
  | 'slide-left'
  | 'slide-right'
  | 'slide-up'
  | 'slide-down'
  | 'scale'
  | 'scale-fade'
  | 'rotate'
  | 'flip';

export interface PageTransitionProps {
  children: React.ReactNode;
  transitionKey: string | number;
  type?: PageTransitionType;
  duration?: number;
  timingFunction?: string;
  onEnter?: () => void;
  onExited?: () => void;
}

const getTransitionStyles = (type: PageTransitionType, distance: number = 30) => {
  switch (type) {
    case 'slide-left':
      return {
        in: { opacity: 1, transform: 'translateX(0)' },
        out: { opacity: 0, transform: `translateX(-${distance}px)` },
        transitionProperty: 'opacity, transform',
      };
    case 'slide-right':
      return {
        in: { opacity: 1, transform: 'translateX(0)' },
        out: { opacity: 0, transform: `translateX(${distance}px)` },
        transitionProperty: 'opacity, transform',
      };
    case 'slide-up':
      return {
        in: { opacity: 1, transform: 'translateY(0)' },
        out: { opacity: 0, transform: `translateY(-${distance}px)` },
        transitionProperty: 'opacity, transform',
      };
    case 'slide-down':
      return {
        in: { opacity: 1, transform: 'translateY(0)' },
        out: { opacity: 0, transform: `translateY(${distance}px)` },
        transitionProperty: 'opacity, transform',
      };
    case 'scale':
      return {
        in: { opacity: 1, transform: 'scale(1)' },
        out: { opacity: 0, transform: 'scale(0.95)' },
        transitionProperty: 'opacity, transform',
      };
    case 'scale-fade':
      return {
        in: { opacity: 1, transform: 'scale(1)' },
        out: { opacity: 0, transform: 'scale(1.05)' },
        transitionProperty: 'opacity, transform',
      };
    case 'rotate':
      return {
        in: { opacity: 1, transform: 'rotate(0deg) scale(1)' },
        out: { opacity: 0, transform: 'rotate(-5deg) scale(0.95)' },
        transitionProperty: 'opacity, transform',
      };
    case 'flip':
      return {
        in: { opacity: 1, transform: 'perspective(1000px) rotateY(0deg)' },
        out: { opacity: 0, transform: 'perspective(1000px) rotateY(90deg)' },
        transitionProperty: 'opacity, transform',
      };
    case 'fade':
    default:
      return 'fade';
  }
};

const PageTransition = forwardRef<HTMLDivElement, PageTransitionProps>(
  (
    {
      children,
      transitionKey,
      type = 'fade',
      duration = 300,
      timingFunction = 'ease-out',
      onEnter,
      onExited,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const [mounted, setMounted] = useState(false);
    const [currentKey, setCurrentKey] = useState(transitionKey);
    const [currentChildren, setCurrentChildren] = useState(children);

    const actualDuration = reducedMotion ? 0 : duration;

    useEffect(() => {
      if (transitionKey !== currentKey) {
        // Start exit animation
        setMounted(false);
      }
    }, [transitionKey, currentKey]);

    useEffect(() => {
      // Initial mount
      setMounted(true);
      onEnter?.();
    }, []);

    const handleExited = () => {
      // After exit, update content and start enter animation
      setCurrentKey(transitionKey);
      setCurrentChildren(children);
      setMounted(true);
      onExited?.();
      onEnter?.();
    };

    return (
      <Transition
        mounted={mounted}
        transition={getTransitionStyles(type)}
        duration={actualDuration}
        timingFunction={timingFunction}
        onExited={handleExited}
      >
        {(styles) => (
          <Box ref={ref} style={styles}>
            {currentChildren}
          </Box>
        )}
      </Transition>
    );
  }
);

PageTransition.displayName = 'PageTransition';

export default PageTransition;
